import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

// Age group options
const AGE_GROUPS = [
  { id: 'age_gen_z', labelKey: 'genZ' },
  { id: 'age_millennials', labelKey: 'millennials' },
  { id: 'age_gen_x', labelKey: 'genX' },
  { id: 'age_boomer', labelKey: 'boomer' },
  { id: 'age_all', labelKey: 'all' },
]

// Interest options
const INTERESTS = [
  { id: 'interest_fitness', labelKey: 'fitness' },
  { id: 'interest_beauty', labelKey: 'beauty' },
  { id: 'interest_fashion', labelKey: 'fashion' },
  { id: 'interest_food', labelKey: 'food' },
  { id: 'interest_travel', labelKey: 'travel' },
  { id: 'interest_sustainability', labelKey: 'sustainability' },
  { id: 'interest_business', labelKey: 'business' },
  { id: 'interest_lifestyle', labelKey: 'lifestyle' },
  { id: 'interest_family', labelKey: 'family' },
  { id: 'interest_tech', labelKey: 'tech' },
  { id: 'interest_art', labelKey: 'art' },
  { id: 'interest_diy', labelKey: 'diy' },
  { id: 'interest_gaming', labelKey: 'gaming' },
  { id: 'interest_music', labelKey: 'music' },
  { id: 'interest_health', labelKey: 'health' },
]

// Lifestyle options
const LIFESTYLES = [
  { id: 'lifestyle_urban', labelKey: 'urban' },
  { id: 'lifestyle_nature', labelKey: 'nature' },
  { id: 'lifestyle_luxury', labelKey: 'luxury' },
  { id: 'lifestyle_budget', labelKey: 'budget' },
  { id: 'lifestyle_family', labelKey: 'family' },
  { id: 'lifestyle_career', labelKey: 'career' },
  { id: 'lifestyle_minimal', labelKey: 'minimal' },
  { id: 'lifestyle_adventure', labelKey: 'adventure' },
]

/**
 * Multi-Select Chip Component
 */
function ChipSelect({ options, selected, onChange, translationPrefix, t }) {
  const toggleOption = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((item) => item !== id))
    } else {
      onChange([...selected, id])
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isSelected = selected.includes(option.id)
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => toggleOption(option.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              isSelected
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t(`${translationPrefix}.${option.labelKey}`)}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Custom Interest Input Component
 */
function CustomInterestInput({ customInterests, onAdd, onRemove, maxCustom = 5, maxLength = 50 }) {
  const { t } = useTranslation('brandProfile')
  const [inputValue, setInputValue] = useState('')

  const handleAdd = () => {
    const trimmed = inputValue.trim()
    if (trimmed && customInterests.length < maxCustom && trimmed.length <= maxLength) {
      onAdd(trimmed)
      setInputValue('')
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  return (
    <div className="mt-3">
      {/* Custom interests display */}
      {customInterests.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {customInterests.map((interest, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-700"
            >
              {interest}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="ml-2 text-purple-500 hover:text-purple-700"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add custom input */}
      {customInterests.length < maxCustom && (
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value.slice(0, maxLength))}
            onKeyDown={handleKeyDown}
            placeholder={t('wizard.steps.target_audience.customInterestPlaceholder')}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={!inputValue.trim()}
            className="px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            + {t('common.add')}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Target Audience Step Component
 *
 * Step 1 of the Brand Profile Wizard.
 * Collects: Age groups, Interests, Lifestyle, Additional notes
 */
export default function TargetAudienceStep({ data, onChange }) {
  const { t } = useTranslation('brandProfile')

  // Extract custom interests (those not in predefined list)
  const predefinedInterestIds = INTERESTS.map((i) => i.id)
  const customInterests = data.interests.filter((i) => !predefinedInterestIds.includes(i))
  const selectedPredefined = data.interests.filter((i) => predefinedInterestIds.includes(i))

  // Handle interest changes
  const handleInterestChange = useCallback(
    (newPredefined) => {
      onChange({ interests: [...newPredefined, ...customInterests] })
    },
    [onChange, customInterests]
  )

  // Add custom interest
  const handleAddCustomInterest = useCallback(
    (interest) => {
      onChange({ interests: [...data.interests, interest] })
    },
    [onChange, data.interests]
  )

  // Remove custom interest
  const handleRemoveCustomInterest = useCallback(
    (index) => {
      const newCustom = customInterests.filter((_, i) => i !== index)
      onChange({ interests: [...selectedPredefined, ...newCustom] })
    },
    [onChange, selectedPredefined, customInterests]
  )

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t('wizard.steps.target_audience.title')}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('wizard.steps.target_audience.subtitle')}
        </p>
      </div>

      {/* Age Groups */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('wizard.steps.target_audience.ageGroups.label')}
        </label>
        <ChipSelect
          options={AGE_GROUPS}
          selected={data.age_groups}
          onChange={(age_groups) => onChange({ age_groups })}
          translationPrefix="wizard.steps.target_audience.ageGroups.options"
          t={t}
        />
      </div>

      {/* Interests */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('wizard.steps.target_audience.interests.label')}
        </label>
        <ChipSelect
          options={INTERESTS}
          selected={selectedPredefined}
          onChange={handleInterestChange}
          translationPrefix="wizard.steps.target_audience.interests.options"
          t={t}
        />
        <CustomInterestInput
          customInterests={customInterests}
          onAdd={handleAddCustomInterest}
          onRemove={handleRemoveCustomInterest}
        />
      </div>

      {/* Lifestyle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('wizard.steps.target_audience.lifestyle.label')}
        </label>
        <ChipSelect
          options={LIFESTYLES}
          selected={data.lifestyle}
          onChange={(lifestyle) => onChange({ lifestyle })}
          translationPrefix="wizard.steps.target_audience.lifestyle.options"
          t={t}
        />
      </div>

      {/* Additional Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('wizard.steps.target_audience.additionalNotes.label')}
          <span className="ml-2 text-gray-400 font-normal">
            ({t('common.optional')})
          </span>
        </label>
        <textarea
          value={data.additional_notes || ''}
          onChange={(e) => onChange({ additional_notes: e.target.value.slice(0, 200) })}
          placeholder={t('wizard.steps.target_audience.additionalNotes.placeholder')}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
        />
        <div className="mt-1 text-xs text-gray-400 text-right">
          {(data.additional_notes || '').length}/200
        </div>
      </div>
    </div>
  )
}
